import type { Column as DatasetColumn } from '@mai/types';

export interface ExtendedColumn extends DatasetColumn {
  key: string; // For React keys
}

export const getTypeBadgeColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    string: 'green',
    integer: 'blue',
    float: 'cyan',
    boolean: 'purple',
    datetime: 'orange',
    json: 'magenta',
    array: 'red',
  };
  return colorMap[type] || 'default';
};

export const createNewColumn = (position: number): ExtendedColumn => ({
  key: `col-${Date.now()}`,
  pos: position,
  name: `column_${position}`,
  source: `Column ${position}`,
  type: 'string',
  nullable: true,
  description: '',
  operations: [],
});

export const prepareColumnsForApi = (columns: ExtendedColumn[]) => {
  return columns.map(({ key: _key, ...col }) => ({
    pos: col.pos,
    name: col.name,
    source: col.source,
    type: col.type,
    nullable: col.nullable,
    description: col.description,
    transformExpr: col.transformExpr,
    operations: col.operations || [],
  }));
};

export const updateColumnPositions = (
  columns: ExtendedColumn[],
): ExtendedColumn[] => {
  return columns.map((col, index) => ({
    ...col,
    pos: index + 1,
  }));
};

export const formatVersionLabel = (
  version: {
    createdAt: Date | string;
    versionNo: number;
    status: string;
  } | null,
): string => {
  if (!version) return '';
  const date =
    typeof version.createdAt === 'string'
      ? new Date(version.createdAt).toLocaleDateString()
      : version.createdAt.toLocaleDateString();
  return `v${version.versionNo} - ${version.status.toUpperCase()} (${date})`;
};

export const processParsingOptions = (formValues: {
  skipRows?: string | number;
  [key: string]: unknown;
}): {
  skipRows: number[];
  headerRow?: number;
  delimiter?: string;
  encoding?: string;
  [key: string]: unknown;
} => {
  const skipRowsValue = formValues.skipRows;
  let skipRows: number[] = [];

  if (skipRowsValue) {
    if (typeof skipRowsValue === 'string') {
      skipRows = skipRowsValue
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s)
        .map((part) => parseInt(part, 10));
      // Remove duplicates while preserving order
      skipRows = [...new Set(skipRows)];
    } else if (typeof skipRowsValue === 'number') {
      skipRows = [skipRowsValue];
    }
  }

  return { ...formValues, skipRows };
};

export const getDefaultParsingOptions = () => ({
  skipRows: [],
  headerRow: 1,
  delimiter: ',',
  encoding: 'utf-8',
});

export const validateSkipRows = (value: string): Promise<void> => {
  if (!value) return Promise.resolve(); // Allow empty

  const parts = value
    .split(',')
    .map((s: string) => s.trim())
    .filter((s: string) => s);
  const dups = new Set<number>();

  for (const part of parts) {
    // check if it's a valid integer (no FLOATS)
    if (!/^\d+$/.test(part)) {
      return Promise.reject(
        new Error('Only non-negative integers allowed (e.g., 0, 1, 2)'),
      );
    }
    const num = parseInt(part, 10);
    if (num < 0) {
      return Promise.reject(
        new Error('Only non-negative integers allowed (e.g., 0, 1, 2)'),
      );
    }
    if (num > 100) {
      return Promise.reject(new Error('Row index cannot exceed 100'));
    }
    if (dups.has(num)) {
      return Promise.reject(
        new Error(
          `Duplicate row index: ${num}. Each row can only be skipped once.`,
        ),
      );
    }
    dups.add(num);
  }
  return Promise.resolve();
};
